/**
 * Trusted Contacts API service — the organization's shared directory of coaches, scouts and other
 * contacts.
 *
 * Multi-tenancy is enforced server-side from the Firebase token; nothing here passes an
 * organization id. The whole surface sits behind the `trusted_contacts` page permission, so every
 * call can come back 403 for a coach whose admin has withheld the page.
 */

import { del, get, post, put } from './api';
import { getCurrentUserToken } from './auth';
import type { PaginatedResponse } from '../types/api';
import type {
  BulkDeleteTrustedContactsResult,
  ImportTrustedContactsResult,
  ParseTrustedContactsCsvResult,
  TrustedContact,
  TrustedContactInput,
  TrustedContactQuery,
} from '../types/trusted-contacts';

/**
 * The wire shape before defaults are applied.
 *
 * `state` and `isFavorite` both shipped after the rest of the record, so a server behind this
 * deploy omits the keys entirely rather than sending them as `null`/`false` — `Partial` on just
 * these two, rather than requiring them, is what makes that legal at the type level.
 */
type RawTrustedContact = Omit<TrustedContact, 'state' | 'isFavorite'> &
  Partial<Pick<TrustedContact, 'state' | 'isFavorite'>>;

/**
 * Applies the defaults a contact must have before the rest of the app touches it, so every other
 * read in this file — and every component downstream — can treat `state` and `isFavorite` as
 * always present rather than re-deriving the "older API" fallback in each place that reads them.
 */
const normalizeTrustedContact = (raw: RawTrustedContact): TrustedContact => ({
  ...raw,
  state: raw.state ?? null,
  isFavorite: raw.isFavorite ?? false,
});

/**
 * The server clamps this too. Repeating it here is not the enforcement — it is so the export,
 * which walks pages, asks for a size the server will actually honour instead of silently getting
 * a smaller one and looping more times than it expected.
 */
export const MAX_PAGE_SIZE = 200;

const buildQuery = (query: TrustedContactQuery): string => {
  const params = new URLSearchParams();

  const search = query.search?.trim();
  if (search) params.set('search', search);
  if (query.contactType) params.set('contactType', query.contactType);

  const organization = query.organization?.trim();
  if (organization) params.set('organization', organization);

  /*
    One `state` parameter per state, which is what the API binds a list from: `?state=OH&state=FL`.
    Appended rather than set, and a single selection produces exactly the request the API accepted
    before this was a list — so a client and a server on either side of that change agree.
  */
  for (const state of query.states ?? []) {
    const trimmed = state.trim();
    if (trimmed) params.append('state', trimmed);
  }

  // Omitted rather than sent `false` — the API is expected to treat its absence as "no
  // favorites-only narrowing" the same way it treats an absent `organization` as "no filter".
  if (query.isFavorite) params.set('isFavorite', 'true');

  if (query.pageNumber !== undefined) params.set('pageNumber', String(query.pageNumber));
  if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));

  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/** One page of the directory. `pageNumber` and `pageSize` in the response are the clamped values. */
export const getTrustedContacts = async (
  query: TrustedContactQuery = {},
  signal?: AbortSignal
): Promise<PaginatedResponse<TrustedContact>> => {
  const result = await get<PaginatedResponse<RawTrustedContact>>(
    `/api/trusted-contacts${buildQuery(query)}`,
    { signal }
  );
  return { ...result, items: result.items.map(normalizeTrustedContact) };
};

/** The distinct schools and clubs in the directory, for the organization filter. */
export interface TrustedContactOrganizationsResult {
  organizations: string[];
  /** True when the server capped the list before every distinct name had been returned. */
  truncated: boolean;
}

/**
 * The raw response shape from `GET /api/trusted-contacts/organizations`.
 *
 * A build ahead of the API it is talking to has to keep working against the bare array the
 * endpoint has always returned, so both shapes are read here rather than assumed — see the note
 * in the PR about web/API deploy skew.
 */
type RawTrustedContactOrganizationsResponse =
  | string[]
  | {
      organizations?: string[];
      items?: string[];
      truncated?: boolean;
      isTruncated?: boolean;
    };

export const getTrustedContactOrganizations = async (
  search?: string,
  signal?: AbortSignal
): Promise<TrustedContactOrganizationsResult> => {
  const params = new URLSearchParams();
  const trimmed = search?.trim();
  // An unknown query parameter is safely ignored by an API that does not read it yet — this is
  // what lets the same client code run against both.
  if (trimmed) params.set('search', trimmed);
  const qs = params.toString();

  const response = await get<RawTrustedContactOrganizationsResponse>(
    `/api/trusted-contacts/organizations${qs ? `?${qs}` : ''}`,
    { signal }
  );

  if (Array.isArray(response)) {
    return { organizations: response, truncated: false };
  }
  return {
    organizations: response.organizations ?? response.items ?? [],
    truncated: Boolean(response.truncated ?? response.isTruncated ?? false),
  };
};

export const createTrustedContact = async (
  input: TrustedContactInput,
  signal?: AbortSignal
): Promise<TrustedContact> =>
  normalizeTrustedContact(
    await post<RawTrustedContact>('/api/trusted-contacts', input, { signal })
  );

export const updateTrustedContact = async (
  id: string,
  input: TrustedContactInput,
  signal?: AbortSignal
): Promise<TrustedContact> =>
  normalizeTrustedContact(
    await put<RawTrustedContact>(`/api/trusted-contacts/${id}`, input, { signal })
  );

/**
 * Flips one contact's favorite flag.
 *
 * Optimistic on the caller's side — the star flips in the UI before this resolves — so a failure
 * here has to be something the caller can catch and revert rather than an unhandled rejection.
 * Callers should expect this to 404/405 against an API deployed before this endpoint existed, and
 * treat that exactly like any other failed toggle.
 */
export const setTrustedContactFavorite = async (
  id: string,
  isFavorite: boolean,
  signal?: AbortSignal
): Promise<TrustedContact> =>
  normalizeTrustedContact(
    await put<RawTrustedContact>(`/api/trusted-contacts/${id}/favorite`, { isFavorite }, { signal })
  );

export const deleteTrustedContact = (id: string, signal?: AbortSignal): Promise<void> =>
  del<void>(`/api/trusted-contacts/${id}`, { signal });

/**
 * Removes several contacts at once.
 *
 * POST rather than DELETE-with-a-body: a request body on DELETE is inconsistently supported across
 * proxies and fetch implementations, and `POST /bulk-delete` is the shape the API already uses for
 * teams. Soft-deletes server-side, like the single-contact path.
 */
export const bulkDeleteTrustedContacts = (
  ids: string[],
  signal?: AbortSignal
): Promise<BulkDeleteTrustedContactsResult> =>
  post<BulkDeleteTrustedContactsResult>('/api/trusted-contacts/bulk-delete', { ids }, { signal });

export const importTrustedContacts = async (
  contacts: TrustedContactInput[],
  signal?: AbortSignal
): Promise<ImportTrustedContactsResult> => {
  const result = await post<Omit<ImportTrustedContactsResult, 'imported'> & {
    imported: RawTrustedContact[];
  }>('/api/trusted-contacts/import', { contacts }, { signal });
  return { ...result, imported: result.imported.map(normalizeTrustedContact) };
};

/**
 * How many pages an export will walk before giving up. 25 × {@link MAX_PAGE_SIZE} = 5,000 rows —
 * past what anyone opens in a spreadsheet, and the ceiling that stops one click becoming an
 * unbounded request loop.
 */
export const MAX_EXPORT_PAGES = 25;

export interface AllTrustedContactsResult {
  contacts: TrustedContact[];
  /** True when the cap stopped the walk before every match had been fetched. */
  truncated: boolean;
  totalCount: number;
}

/**
 * Every contact matching `query`, walking the pages the list endpoint returns.
 *
 * The export has to be the whole filtered directory rather than the page on screen — a coach who
 * exports 87 contacts and opens a file holding 10 has been handed the wrong file. The paging
 * fields are excluded from the parameter type because this function sets them itself; accepting
 * them would let a caller believe they could bound the export and then silently ignore it.
 */
export const getAllTrustedContacts = async (
  query: Omit<TrustedContactQuery, "pageNumber" | "pageSize"> = {},
  signal?: AbortSignal
): Promise<AllTrustedContactsResult> => {
  const contacts: TrustedContact[] = [];
  let totalCount = 0;

  for (let page = 1; page <= MAX_EXPORT_PAGES; page++) {
    const result = await getTrustedContacts(
      { ...query, pageNumber: page, pageSize: MAX_PAGE_SIZE },
      signal
    );
    totalCount = result.totalCount;
    contacts.push(...result.items);

    if (!result.hasMore) {
      return { contacts, truncated: false, totalCount };
    }
  }

  return { contacts, truncated: true, totalCount };
};

/**
 * How many contacts one import request carries.
 *
 * Matches `ImportTrustedContactsRequest.MaxContacts` on the API. A spreadsheet routinely holds more
 * than this, so {@link importTrustedContactsInChunks} walks it in batches — which is safe precisely
 * because the import deduplicates: a retried or partially-applied batch skips what already landed
 * rather than doubling it.
 */
export const IMPORT_CHUNK_SIZE = 200;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';
const ACTIVE_ORG_STORAGE_KEY = 'activeOrganizationId';

/**
 * Reads a contacts spreadsheet, using the LLM on the server to work out which column is which.
 *
 * Multipart rather than the shared JSON client, which sets `Content-Type: application/json` on
 * every request — that header would make the server reject the body before it saw the file. Same
 * shape as `crmImport.uploadImportFile`.
 *
 * **This writes nothing.** It answers what is in the file; the caller commits separately.
 */
export const parseTrustedContactsCsv = async (
  file: File,
  signal?: AbortSignal
): Promise<ParseTrustedContactsCsvResult> => {
  const token = await getCurrentUserToken();
  const formData = new FormData();
  formData.append('file', file);

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const orgOverrideId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  if (orgOverrideId) headers['X-Organization-Id'] = orgOverrideId;

  const response = await fetch(`${API_BASE_URL}/api/trusted-contacts/parse-csv`, {
    method: 'POST',
    headers,
    body: formData,
    signal,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    // The server's message is the actionable one — "that looks like an Excel workbook renamed to
    // .csv" tells the coach what to do; a status code does not.
    throw new Error(
      (body as { message?: string }).message ?? `Could not read that file (${response.status}).`
    );
  }

  return (await response.json()) as ParseTrustedContactsCsvResult;
};

/**
 * Thrown when a batched commit fails part-way, carrying what had already landed.
 *
 * Without this the caller can only say "it failed" about a run that wrote 400 of 1,000 contacts,
 * and the coach reasonably concludes nothing happened.
 */
export class PartialImportError extends Error {
  readonly result: ImportTrustedContactsResult;

  constructor(message: string, result: ImportTrustedContactsResult, options?: ErrorOptions) {
    super(message, options);
    this.name = "PartialImportError";
    this.result = result;
  }
}

/**
 * Commits parsed rows through the ordinary import, in batches the API will accept.
 *
 * Counts are summed from the server's own numbers rather than recomputed from the arrays, so they
 * stay right if the API ever reports a count without the full list.
 *
 * A batch that fails does not undo the ones before it — and it does not need to: the import
 * deduplicates on phone and email, so re-running skips what landed. What the caller must not do is
 * report total failure, which is why the partial result is attached to the error.
 */
export const importTrustedContactsInChunks = async (
  contacts: TrustedContactInput[],
  signal?: AbortSignal
): Promise<ImportTrustedContactsResult> => {
  const combined: ImportTrustedContactsResult = {
    imported: [],
    duplicates: [],
    skipped: [],
    importedCount: 0,
    duplicateCount: 0,
  };

  for (let start = 0; start < contacts.length; start += IMPORT_CHUNK_SIZE) {
    try {
      const batch = await importTrustedContacts(
        contacts.slice(start, start + IMPORT_CHUNK_SIZE),
        signal
      );

      combined.imported.push(...batch.imported);
      combined.duplicates.push(...batch.duplicates);
      combined.skipped.push(...batch.skipped);
      combined.importedCount += batch.importedCount;
      combined.duplicateCount += batch.duplicateCount;
    } catch (error) {
      if (combined.importedCount === 0) throw error;
      throw new PartialImportError(
        error instanceof Error ? error.message : "Could not import those contacts.",
        combined,
        { cause: error }
      );
    }
  }

  return combined;
};
